import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from '@unform/web';
import { useSelector, useDispatch } from 'react-redux';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
// icons
import { FaRegFileExcel } from 'react-icons/fa';
import {
  FiTrash2,
  FiInfo,
  FiDownload,
  FiGitMerge,
  FiCalendar,
  FiMail,
} from 'react-icons/fi';

// servicos
import api from 'services/api';
import AppError from 'utils/AppError';

// hooks
import { useModal } from 'hooks/modal';

// actions
import { show } from 'store/modules/dialog/actions';

// componentes
import Button from 'components/Button';
import Upload from 'components/Upload';
import Card from 'components/Card';
import Loader from 'components/ContentLoader/GenericLoader';
import Pagination from 'components/Pagination';
import Radio from 'components/Radio';
import Flag from 'components/Flag';

// estilos
import { Container, Table } from './styles';
import { Scroll } from 'styles/global';
import { downloadFile } from 'utils/index';
import { Permissao, Role, StatusRoteiro } from 'utils/variables';
import { Emissora } from 'pages/@types';
import { usePermit } from 'utils/hooks/usePermit';
import DropDownMenu from 'components/DropDownMenu';
import Spinner from 'components/Spinner';
import Tooltip from 'components/Tooltip';

interface IUploadFile {
  id: string;
  file: File;
  name: string;
  readableSize: string;
  progress: number;
  uploaded: boolean;
  error: boolean;
}

interface IRoteiro {
  id: number;
  filename: string;
  originalfilename: string;
  status:
    | 'pendente'
    | 'convertendo'
    | 'convertido'
    | 'erroconversao'
    | 'cadastrando'
    | 'cadastrado'
    | 'errocadastro';
  statusanalise: 'pendente' | 'analisando' | 'analisado' | 'erro' | 'NA';
  roteiroconvertido_url: string;
  datautilizacao: string;
  created_at: string;
  excelfilename?: string;
  emailsent_at?: Date;
}
export default function Roteiro() {
  //USE STATE
  const [loading, setLoading] = useState(false);
  const [roteiros, setRoteiros] = useState<IRoteiro[]>([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [selected, setSelected] = useState({} as IRoteiro);
  const [selectedFilesExcel, setSelectedFilesExcel] = useState<any[]>([]);
  const [selectedUpload, setSelectedUpload] = useState({} as IRoteiro);
  const [emissoras, setEmissoras] = useState<Emissora[]>([]);
  const [loadingEmissoras, setLoadingEmissoras] = useState(false);
  const [loadingUploadExcel, setLoadingUploadExcel] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);

  // USE REF
  const inputFileRef = useRef<HTMLInputElement>(null);

  //REDUX
  const { emissora } = useSelector((state: any) => state.usuario);
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  // REACT HOOKS
  const hasOperacaoRemota = usePermit([], [Permissao.OPERACAOREMOTA]);
  const hasOrientacaoExibicao = usePermit([Role.OPEC, Role.EXIB], []);
  const { showModal, hideModal } = useModal();
  const navigate = useNavigate(); // react-router-dom

  // FUNÇÕES
  function handleDelete(roteiro: IRoteiro) {
    setSelected(roteiro);
    const optionBase = {
      type: 'question',
      text: `Apagar roteiro de ${format(
        new Date(roteiro.datautilizacao),
        "dd 'de' MMMM'",
        { locale: pt },
      )} ?`,
      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/roteiros/${roteiro.id}`,
        method: api.delete,
        data: { arquivado: true },
      },
      haveCallback: true,
      successMessage: 'Roteiro apagado!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };
    dispatch(show(optionBase));
  }

  // USE CALLBACKS
  const handleSubmitOpRemota = useCallback(
    async (data, id) => {
      try {
        if (!data.emissora) return toast.error('Informe a emissora de destino');
        const response = await api.post(`/operacaoremota/roteiro/${id}`, {
          emissoradestino: Number(data.emissora),
        });
        if (response && response.data) {
          toast.success('Envios criados');
        } else toast.error('Falha ao criar envios');

        hideModal();
      } catch (err) {
        console.log(err);
        if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
    },
    [hideModal],
  );
  const handleOperacaoRemota = useCallback(
    id => {
      if (emissoras && emissoras.length > 0) {
        showModal({
          title: 'Selecionar a emissora',
          component: (
            <Form onSubmit={data => handleSubmitOpRemota(data, id)}>
              <Radio
                name="emissora"
                options={emissoras
                  .filter(e => e.id !== emissora.value)
                  .map(e => ({ id: e.id, label: e.nome }))}
                disabled={loading || loadingEmissoras}
              />
              <Button type="submit" color="primary">
                CONFIRMAR
              </Button>
            </Form>
          ),
          width: '50vw',
        });
      } else toast.error('Falha ao carregar emissoras');
    },
    [
      emissora,
      emissoras,
      handleSubmitOpRemota,
      loading,
      loadingEmissoras,
      showModal,
    ],
  );

  const handleDownload = useCallback(async (url: string, filename: string) => {
    try {
      await downloadFile({
        url,
        name: filename,
        fromAPI: true,
      });
    } catch (error) {
      toast.error('Falha ao baixar o roteiro.');
    }
  }, []);

  const handleEmail = useCallback(async id => {
    try {
      setLoadingEmail(true);
      const response = await api.post(`/roteiros/${id}/sendemail`);
      if (response?.status === 200) {
        toast.success('E-mail enviado com sucesso');
        setRoteiros(prevState =>
          prevState.map(c =>
            c.id === id ? { ...c, emailsent_at: new Date() } : c,
          ),
        );
      } else toast.error('Falha ao enviar e-mail');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message);
      }
    } finally {
      setLoadingEmail(false);
    }
  }, []);

  const handleSuccessMessage = useCallback(() => {
    const option = {
      type: 'success',
      confirmButtonText: '',
      contentHTML: 'Roteiro adicionado',
      timer: 3000,
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
    };
    dispatch(show(option));
  }, [dispatch]);

  const handleActionSelect = useCallback(
    (roteiro, action) => {
      switch (action.value) {
        case 1:
          handleDelete(roteiro);
          break;
        case 2:
          handleDownload(roteiro.roteiroconvertido_url, roteiro.filename);
          break;
        case 3:
          handleOperacaoRemota(roteiro.id);
          break;
        default:
          break;
      }
    },
    [handleDelete],
  );

  // #region  Upload TXT
  const handleUpload = useCallback((files: File[]) => {
    setSelectedFiles(
      files.map(file => ({
        id: uniqueId(),
        file,
        name: file.name,
        readableSize: filesize(file.size),
        progress: 0,
        uploaded: false,
        error: false,
      })),
    );
  }, []);

  const updateFile = (id: string, data: Partial<IUploadFile>) => {
    setSelectedFiles(prev =>
      prev.map(file => (file.id === id ? { ...file, ...data } : file)),
    );
  };

  const processUpload = useCallback(
    async (selectedFile: IUploadFile) => {
      try {
        const data = new FormData();
        data.append('file', selectedFile.file);

        const response = await api.post(
          `/emissoras/${emissora.value}/roteiros`,
          data,
          {
            onUploadProgress: e => {
              if (e.total)
                updateFile(selectedFile.id, {
                  progress: Math.round((e.loaded * 100) / e.total),
                });
            },
          },
        );

        updateFile(selectedFile.id, { uploaded: true });
        toast.success('Roteiro adicionado');

        const newRoteiro: IRoteiro = {
          ...response.data,
          status: 'pendente',
          statusanalise: 'NA',
          created_at: response.data.createdAt,
        };

        setRoteiros(prev => [newRoteiro, ...prev]);
        handleSuccessMessage();
      } catch (error) {
        updateFile(selectedFile.id, { error: true });
        toast.error('Falha ao enviar arquivo');
      } finally {
        setTimeout(() => setSelectedFiles([]), 2000);
      }
    },
    [emissora],
  );

  useEffect(() => {
    selectedFiles.forEach(f => {
      if (!f.uploaded && !f.error && f.progress === 0) processUpload(f);
    });
  }, [selectedFiles, processUpload]);
  //#endregion

  // #region  Upload XLS
  const handleUploadExcel = useCallback((files: File[]) => {
    setSelectedFilesExcel(
      files.map(file => ({
        id: uniqueId(),
        file,
        name: file.name,
        readableSize: filesize(file.size),
        progress: 0,
        uploaded: false,
        error: false,
      })),
    );
  }, []);

  const updateFileExcel = (id: string, data: Partial<IUploadFile>) => {
    setSelectedFilesExcel(prev =>
      prev.map(file => (file.id === id ? { ...file, ...data } : file)),
    );
  };

  const processUploadExcel = useCallback(
    async (selectedFile: IUploadFile) => {
      try {
        const data = new FormData();
        data.append('file', selectedFile.file);
        setLoadingUploadExcel(true);
        const response = await api.post(
          `/roteiros/${selectedUpload.id}/uploadexcel`,
          data,
          {
            onUploadProgress: e => {
              if (e.total)
                updateFile(selectedFile.id, {
                  progress: Math.round((e.loaded * 100) / e.total),
                });
            },
          },
        );

        if (response?.data) {
          updateFileExcel(selectedFile.id, { uploaded: true });
          toast.success('Roteiro adicionado');
          setRoteiros(prevState =>
            prevState.map(p =>
              p.id === selectedUpload.id ? response.data : p,
            ),
          );
        }
      } catch (error) {
        updateFile(selectedFile.id, { error: true });
        toast.error('Falha ao enviar arquivo');
      } finally {
        setTimeout(() => setSelectedFiles([]), 2000);
        setLoadingUploadExcel(false);
      }
    },
    [emissora, selectedUpload],
  );

  useEffect(() => {
    selectedFilesExcel.forEach(f => {
      if (!f.uploaded && !f.error && f.progress === 0) processUploadExcel(f);
    });
  }, [selectedFilesExcel]);
  //#endregion

  // USE EFFECT
  useEffect(() => {
    const controller = new AbortController();
    async function buscaRoterios() {
      setLoading(true);

      try {
        const response = await api.get(`/roteiros`, {
          signal: controller.signal,
          params: { page, emissora: emissora.value },
        });
        if (response && response.data) {
          setRoteiros(response.data.roteiros);
          setPages(response.data.pages);
        }
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoading(false);
      }
    }

    const options = {
      type: 'warning',
      contentHTML: `<h4>ATENÇÃO<h4><h1>PRAÇA SELECIONADA: ${emissora.nome ||
        emissora.label}</h1>`,
      allowOutsideClick: false,
      cancelButtonText: 'OK',
      showConfirmButton: false,
    };
    dispatch(show(options));
    buscaRoterios();
    return () => {
      controller.abort();
    };
  }, [dispatch, emissora, page]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadEmissoras() {
      setLoadingEmissoras(true);
      try {
        const response = await api.get('/emissoras');
        if (response && response.data) {
          setEmissoras(response.data);
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingEmissoras(false);
      }
    }
    loadEmissoras();
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'success' && selected) {
        setRoteiros(prevState => prevState.filter(c => c.id !== selected.id));
      }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <header>
        <Upload
          name="Upload Roteiro"
          disabled={false}
          onUpload={handleUpload}
          selectedFiles={selectedFiles}
          accept={{ 'text/plain': ['.txt'] }}
        />
        <Button
          color="primary"
          href={'/orientacoesexibicao'}
          disabled={!hasOrientacaoExibicao}
        >
          <FiCalendar />
          Orientações
        </Button>
      </header>
      <Card>
        {loading && <Loader />}
        {!!roteiros.length && !loading && (
          <>
            <header>
              <h4>LISTA DE ROTEIROS</h4>
              <Pagination
                total={pages}
                activePage={page}
                onClick={page => setPage(page)}
              />
              <div style={{ width: 200 }} />
            </header>
            <Scroll>
              <Table>
                <thead>
                  <tr>
                    <th>TÍTULO</th>
                    <th>STATUS</th>
                    <th>ELEIÇÕES</th>
                    <th>UTILIZAÇÃO</th>
                    <th>DATA DE CRIAÇÃO</th>
                    <th>AÇÕES</th>
                  </tr>
                </thead>

                <tbody>
                  {roteiros.map((r, i) => (
                    <tr key={r.id}>
                      <td>{r.filename || r.originalfilename}</td>
                      <td>
                        <Flag
                          name={r.status}
                          type={r.status}
                          icon={
                            r.status === StatusRoteiro.CADASTRADO ? (
                              <FiInfo />
                            ) : null
                          }
                          handleAction={() => navigate(`/roteiros/${r.id}`)}
                        />
                      </td>
                      <td>
                        <Flag
                          name={r.statusanalise}
                          type={r.statusanalise}
                          icon={
                            r.statusanalise === StatusRoteiro.ANALISADO ||
                            r.statusanalise === StatusRoteiro.ERRO ? (
                              <FiInfo />
                            ) : null
                          }
                          handleAction={() =>
                            navigate(`/roteiroseleicao/${r.id}`)
                          }
                        />
                      </td>
                      <td>
                        {format(
                          new Date(r.datautilizacao),
                          "dd 'de' MMMM 'de' yyyy",
                          { locale: pt },
                        )}
                      </td>
                      <td>
                        {format(
                          new Date(r.created_at),
                          "dd 'de' MMMM', às' HH:mm",
                          { locale: pt },
                        )}
                      </td>
                      <td>
                        <Button
                          id={`id_enviar_email${r.id}`}
                          fill
                          round
                          color={r.emailsent_at ? 'success' : 'font'}
                          disabled={
                            !r.excelfilename ||
                            r.status === StatusRoteiro.PENDENTE ||
                            r.status === StatusRoteiro.ERRO ||
                            loadingEmail
                          }
                          onClick={() => handleEmail(r.id)}
                        >
                          <Tooltip target={`id_enviar_email${r.id}`}>
                            ENVIAR EMAIL
                          </Tooltip>
                          <FiMail />
                        </Button>

                        <input
                          type="file"
                          ref={inputFileRef}
                          style={{ display: 'none' }}
                          onChange={e => {
                            if (!e.target.files || !selectedUpload) return;

                            const filesArray = Array.from(e.target.files);
                            handleUploadExcel(filesArray);
                          }}
                        />
                        {loadingUploadExcel && selectedUpload.id === r.id ? (
                          <Spinner />
                        ) : (
                          <Button
                            id={`id_upload_excel${r.id}`}
                            fill
                            round
                            color={r.excelfilename ? 'success' : 'font'}
                            onClick={() => {
                              setSelectedUpload(r);
                              if (inputFileRef && inputFileRef.current)
                                inputFileRef.current.click();
                            }}
                          >
                            <Tooltip target={`id_upload_excel${r.id}`}>
                              UPLOAD EXCEL
                            </Tooltip>
                            <FaRegFileExcel />
                          </Button>
                        )}
                        <DropDownMenu
                          id={`acoes${i}`}
                          disabled={false}
                          actions={[
                            {
                              value: 1,
                              label: 'APAGAR',
                              icon: <FiTrash2 />,
                            },
                            {
                              value: 2,
                              label: 'DOWNLOAD',
                              icon: <FiDownload />,
                              disabled: !r.roteiroconvertido_url,
                            },
                            {
                              value: 3,
                              label: 'OP. REMOTA',
                              disabled:
                                !hasOperacaoRemota ||
                                r.status !== StatusRoteiro.CADASTRADO,
                              icon: <FiGitMerge />,
                            },
                          ]}
                          handleClick={action =>
                            !action.disabled && handleActionSelect(r, action)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Scroll>
          </>
        )}
      </Card>
    </Container>
  );
}
